import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Tag,
  Popconfirm,
  Alert,
  Divider,
  Typography,
  Tooltip,
  Badge
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  RollbackOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import { useTranslation } from '../../../shared/i18n';
import I18nFieldTabs, { parseI18n, cleanI18n, mergeI18n } from '../../components/I18nFieldTabs';
import { CATEGORY_ICONS } from '../../../shared/constants/wafRules';

// 2026-10-21 起 sqli/xss/path_traversal 的规则也下发边缘(签名引擎,libinjection 照常并行),可编辑。
const BUILTIN_ENGINE_KEYS = [];

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

// 严重程度选项
const SEVERITY_OPTIONS = [
  { value: 'high', label: '高危', color: 'red' },
  { value: 'medium', label: '中危', color: 'orange' },
  { value: 'low', label: '低危', color: 'blue' }
];

// 分类选项
const CATEGORY_OPTIONS = [
  { value: 'injection', label: '注入攻击', icon: CATEGORY_ICONS.injection },
  { value: 'file', label: '文件安全', icon: CATEGORY_ICONS.file },
  { value: 'protocol', label: '协议安全', icon: CATEGORY_ICONS.protocol },
  { value: 'scanner', label: '扫描器防护', icon: CATEGORY_ICONS.scanner },
  { value: 'bot', label: '机器人防护', icon: CATEGORY_ICONS.bot },
  { value: 'additional', label: '附加防护', icon: CATEGORY_ICONS.additional }
];

export default function WafRulesets() {
  const [loading, setLoading] = useState(false);
  const [rulesets, setRulesets] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(1);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRuleset, setEditingRuleset] = useState(null);
  const [rulesJsonMode, setRulesJsonMode] = useState(true); // JSON编辑模式
  const [form] = Form.useForm();
  const { t } = useTranslation();

  useEffect(() => {
    loadRulesets();
    loadVersion();
  }, []);

  // 加载所有规则集
  const loadRulesets = async () => {
    setLoading(true);
    try {
      const response = await cdnService.getWafRulesetsAdmin();
      if (response.success) {
        setRulesets(response.data);
      } else {
        message.error(response.error || '加载失败');
      }
    } catch (error) {
      message.error('加载规则集失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 加载当前版本号
  const loadVersion = async () => {
    try {
      const response = await cdnService.getWafRulesetsVersion();
      if (response.success) {
        setCurrentVersion(response.data.version);
      }
    } catch (error) {
      console.error('Failed to load version:', error);
    }
  };

  // 打开创建/编辑对话框
  const handleOpenModal = (ruleset = null) => {
    setEditingRuleset(ruleset);
    if (ruleset) {
      // 将rules数组转换为JSON字符串用于编辑
      const rulesJson = Array.isArray(ruleset.rules)
        ? JSON.stringify(ruleset.rules, null, 2)
        : '[]';

      form.setFieldsValue({
        key: ruleset.key,
        name: ruleset.name,
        description: ruleset.description,
        admin_note: ruleset.admin_note || '',
        category: ruleset.category,
        severity: ruleset.severity,
        rules: rulesJson,
        i18n: parseI18n(ruleset.i18n)
      });
    } else {
      // 默认模板
      const defaultRules = [{
        rule_id: 1000,
        enabled: true,
        phase: 'request',
        match: {
          type: 'regex',
          collection: 'QS_ARGS',
          pattern: '',
          transform: 'none'
        },
        action: {
          type: 'block',
          status: 403,
          score: 10,
          log: true,
          message: ''
        }
      }];

      form.setFieldsValue({
        key: '',
        name: '',
        description: '',
        admin_note: '',
        category: 'injection',
        severity: 'high',
        rules: JSON.stringify(defaultRules, null, 2),
        i18n: {}
      });
    }
    setModalVisible(true);
  };

  // 关闭对话框
  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingRuleset(null);
    form.resetFields();
  };

  // 保存规则集
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // 解析JSON格式的rules
      let rulesArray;
      try {
        rulesArray = JSON.parse(values.rules);
        if (!Array.isArray(rulesArray)) {
          message.error('规则列表必须是JSON数组格式');
          return;
        }

        // 验证每个规则的基本结构
        for (const rule of rulesArray) {
          if (!rule.rule_id || !rule.match || !rule.action) {
            message.error('每条规则必须包含 rule_id、match 和 action 字段');
            return;
          }
        }
      } catch (e) {
        message.error('规则JSON格式错误: ' + e.message);
        return;
      }

      const data = {
        ...values,
        rules: rulesArray,
        i18n: cleanI18n(mergeI18n(editingRuleset?.i18n, values.i18n))
      };

      let response;
      if (editingRuleset) {
        // 更新
        response = await cdnService.updateWafRuleset(editingRuleset.id, data);
      } else {
        // 创建
        response = await cdnService.createWafRuleset(data);
      }

      if (response.success) {
        message.success(editingRuleset ? '更新成功' : '创建成功');
        handleCloseModal();
        loadRulesets();
        loadVersion();
      } else {
        message.error(response.error || '保存失败');
      }
    } catch (error) {
      if (error.errorFields) {
        message.error('请填写所有必填字段');
      } else {
        message.error('保存失败: ' + error.message);
      }
    }
  };

  // 删除规则集（软删除）
  const handleDelete = async (id) => {
    try {
      const response = await cdnService.deleteWafRuleset(id);
      if (response.success) {
        message.success('规则集已禁用');
        loadRulesets();
        loadVersion();
      } else {
        message.error(response.error || '删除失败');
      }
    } catch (error) {
      message.error('删除失败: ' + error.message);
    }
  };

  // 恢复规则集
  const handleRestore = async (id) => {
    try {
      const response = await cdnService.restoreWafRuleset(id);
      if (response.success) {
        message.success('规则集已恢复');
        loadRulesets();
        loadVersion();
      } else {
        message.error(response.error || '恢复失败');
      }
    } catch (error) {
      message.error('恢复失败: ' + error.message);
    }
  };

  // 获取严重程度标签
  const getSeverityTag = (severity) => {
    const option = SEVERITY_OPTIONS.find(o => o.value === severity);
    return <Tag color={option?.color}>{option?.label || severity}</Tag>;
  };

  // 获取分类信息
  const getCategoryInfo = (category) => {
    const option = CATEGORY_OPTIONS.find(o => o.value === category);
    return option ? <span>{option.icon} {option.label}</span> : category;
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60
    },
    {
      title: '标识',
      dataIndex: 'key',
      key: 'key',
      width: 150,
      render: (key) => (
        <Space size={4}>
          <Text code>{key}</Text>
          {BUILTIN_ENGINE_KEYS.includes(key) && <Tag color="blue">内置引擎</Tag>}
        </Space>
      )
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      render: (name, r) => (
        <div>
          <div>{name}</div>
          {r.admin_note && (
            <Tooltip title={r.admin_note}>
              <div style={{ color: '#999', fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>备注：{r.admin_note}</div>
            </Tooltip>
          )}
        </div>
      )
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (category) => getCategoryInfo(category)
    },
    {
      title: '严重程度',
      dataIndex: 'severity',
      key: 'severity',
      width: 100,
      render: (severity) => getSeverityTag(severity)
    },
    {
      title: '规则数',
      dataIndex: 'rules',
      key: 'rules_count',
      width: 80,
      render: (rules) => {
        const count = Array.isArray(rules) ? rules.length : 0;
        return <Badge count={count} showZero color="blue" />;
      }
    },
    {
      title: '版本',
      dataIndex: 'version',
      key: 'version',
      width: 80,
      render: (version) => <Tag color="purple">v{version}</Tag>
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      render: (enabled) => enabled ? (
        <Tag icon={<CheckCircleOutlined />} color="success">启用</Tag>
      ) : (
        <Tag icon={<CloseCircleOutlined />} color="error">禁用</Tag>
      )
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 160
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="编辑">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleOpenModal(record)}
              size="small"
            />
          </Tooltip>
          {record.enabled ? (
            <Popconfirm
              title="确定要禁用此规则集吗？"
              description="禁用后将递增版本号，CDN会自动同步"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Tooltip title="禁用">
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                />
              </Tooltip>
            </Popconfirm>
          ) : (
            <Tooltip title="恢复">
              <Button
                type="link"
                icon={<RollbackOutlined />}
                onClick={() => handleRestore(record.id)}
                size="small"
              />
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>WAF规则集管理</Title>
            <Tag color="blue">当前版本: v{currentVersion}</Tag>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                loadRulesets();
                loadVersion();
              }}
            >
              刷新
            </Button>
          </Space>
        }
      >
        <Alert
          message="规则集版本管理"
          description="修改、创建或禁用规则集会自动递增全局版本号。CDN节点会在1-5分钟内自动同步最新规则集。"
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 16 }}
        />

        <Table
          columns={columns}
          dataSource={rulesets}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showTotal: (total) => `共 ${total} 条`,
            showSizeChanger: true
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* 创建/编辑对话框 */}
      <Modal
        title={editingRuleset ? '编辑规则集' : '创建规则集'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={handleCloseModal}
        width={700}
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="key"
            label="规则集标识"
            rules={[
              { required: true, message: '请输入规则集标识' },
              { pattern: /^[a-z_]+$/, message: '只能使用小写字母和下划线' }
            ]}
            extra="唯一标识，使用小写字母和下划线，如: csrf_protection"
          >
            <Input
              placeholder="例如: csrf_protection"
              disabled={!!editingRuleset}
            />
          </Form.Item>

          <I18nFieldTabs
            fields={[{ key: 'name', label: t('Name') }, { key: 'description', label: t('Description'), type: 'textarea' }]}
            base={<>
          <Form.Item
            name="name"
            label="规则集名称"
            rules={[{ required: true, message: '请输入规则集名称' }]}
          >
            <Input placeholder="例如: CSRF防护" />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
            rules={[{ required: true, message: '请输入描述' }]}
          >
            <TextArea
              rows={2}
              placeholder="一句话说明拦什么（用户可见），例如：拦截 SQL 注入攻击"
            />
          </Form.Item>

          <Form.Item name="admin_note" label="备注（仅后台可见）">
            <TextArea rows={3} placeholder="技术细节：来源规则编号、覆盖的攻击手法等，用户看不到" />
          </Form.Item>
            </>}
          />

          <Form.Item
            name="category"
            label="分类"
            rules={[{ required: true, message: '请选择分类' }]}
          >
            <Select placeholder="选择分类">
              {CATEGORY_OPTIONS.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.icon} {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="severity"
            label="严重程度"
            rules={[{ required: true, message: '请选择严重程度' }]}
          >
            <Select placeholder="选择严重程度">
              {SEVERITY_OPTIONS.map(option => (
                <Option key={option.value} value={option.value}>
                  <Tag color={option.color}>{option.label}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          {!BUILTIN_ENGINE_KEYS.includes(editingRuleset?.key) && (
          <Form.Item
            name="rules"
            label="规则定义 (JSON格式)"
            rules={[{ required: true, message: '请输入规则定义' }]}
            extra={
              <div>
                <div>完整的规则定义JSON数组。每条规则需包含：</div>
                <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                  <li><Text code>rule_id</Text>: 规则ID (数字)</li>
                  <li><Text code>enabled</Text>: 是否启用 (true/false)</li>
                  <li><Text code>phase</Text>: 执行阶段 (request/response)</li>
                  <li><Text code>match</Text>: 匹配条件对象 (type, collection, pattern等)</li>
                  <li><Text code>action</Text>: 动作对象 (type, status, score, log, message)</li>
                </ul>
                <div style={{ marginTop: 8 }}>
                  <a
                    href="https://example.com/waf-rules-doc"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    查看规则格式文档
                  </a>
                </div>
              </div>
            }
          >
            <TextArea
              rows={12}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
              placeholder={JSON.stringify([{
                rule_id: 1000,
                enabled: true,
                phase: 'request',
                match: {
                  type: 'regex',
                  collection: 'QS_ARGS',
                  pattern: '(?i)(select|union|insert)',
                  transform: 'none'
                },
                action: {
                  type: 'block',
                  status: 403,
                  score: 10,
                  log: true,
                  message: 'SQL injection detected'
                }
              }], null, 2)}
            />
          </Form.Item>
          )}
        </Form>

        <Alert
          message="提示"
          description={BUILTIN_ENGINE_KEYS.includes(editingRuleset?.key)
            ? "内置引擎类规则集只能开关，规则由边缘引擎内置。"
            : "保存后约 1 分钟内在所有节点生效。"}
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Modal>
    </div>
  );
}
